/** WebSocket client: single connection, ref-counted channel subscriptions, auto-reconnect. */
import { useEffect, useRef } from 'react';
import { WS_URL } from './config';
import { createStore, useStore } from './store';

type Handler = (data: any) => void;
export type WsStatus = 'connecting' | 'live' | 'reconnecting';

export const wsState = createStore<{ status: WsStatus; latencyMs: number | null }>({ status: 'connecting', latencyMs: null });

class WsClient {
  private sock: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private token: string | null = null;
  private attempt = 0;
  private pingTimer: number | null = null;
  private lastPing = 0;
  /** Page reported for presence (signed-in users); re-sent after every (re)authentication. */
  private page: string | null = null;

  constructor(private readonly url: string) {
    this.connect();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this.sock) this.connect();
    });
  }

  private connect() {
    let s: WebSocket;
    try {
      s = new WebSocket(this.url);
    } catch {
      this.retry();
      return;
    }
    this.sock = s;
    s.onopen = () => {
      this.attempt = 0;
      wsState.set({ status: 'live' });
      if (this.token) this.raw({ op: 'auth', token: this.token });
      const chans = [...this.handlers.keys()];
      if (chans.length) this.raw({ op: 'subscribe', ch: chans });
      this.pingTimer = window.setInterval(() => {
        this.lastPing = performance.now();
        this.raw({ op: 'ping' });
      }, 15_000);
    };
    s.onmessage = (ev) => {
      let msg: { ch: string; data: any };
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.ch === 'sys') {
        if (msg.data?.type === 'pong' && this.lastPing) wsState.set({ latencyMs: Math.round(performance.now() - this.lastPing) });
        if (msg.data?.type === 'authed') {
          // Re-subscribe user channels now that the socket is authenticated.
          const user = [...this.handlers.keys()].filter((c) => c.startsWith('user:'));
          if (user.length) this.raw({ op: 'subscribe', ch: user });
          if (this.page) this.raw({ op: 'presence', page: this.page });
        }
        return;
      }
      const hs = this.handlers.get(msg.ch);
      if (hs) for (const h of hs) h(msg.data);
    };
    s.onclose = () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      this.sock = null;
      this.retry();
    };
  }

  private retry() {
    wsState.set({ status: 'reconnecting' });
    const delay = Math.min(15_000, 500 * 2 ** Math.min(this.attempt++, 5)) + Math.random() * 300;
    window.setTimeout(() => this.connect(), delay);
  }

  private raw(msg: unknown) {
    if (this.sock?.readyState === WebSocket.OPEN) this.sock.send(JSON.stringify(msg));
  }

  /** Tell the server which page the user is on (presence for the back office). Pings keep it alive. */
  setPresence(page: string) {
    if (page === this.page) return;
    this.page = page;
    if (this.token) this.raw({ op: 'presence', page });
  }

  setToken(token: string | null) {
    if (token === this.token) return;
    this.token = token;
    // Signed out (or the session ended): the open connection must stop counting as the user, or its
    // pings keep them Online in the back office for as long as the tab stays open.
    this.raw(token ? { op: 'auth', token } : { op: 'logout' });
  }

  subscribe(ch: string, h: Handler): () => void {
    let set = this.handlers.get(ch);
    if (!set) {
      set = new Set();
      this.handlers.set(ch, set);
      this.raw({ op: 'subscribe', ch: [ch] });
    }
    set.add(h);
    return () => {
      const s = this.handlers.get(ch);
      if (!s) return;
      s.delete(h);
      if (!s.size) {
        this.handlers.delete(ch);
        this.raw({ op: 'unsubscribe', ch: [ch] });
      }
    };
  }
}

export const ws = new WsClient(WS_URL);

/** Subscribe to a channel for the component's lifetime. The latest handler is always used. */
export function useChannel<T = any>(ch: string | null | undefined, handler: (data: T) => void) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!ch) return;
    return ws.subscribe(ch, (d) => ref.current(d));
  }, [ch]);
}

export const useWsStatus = () => useStore(wsState, (s) => s.status);
export const useWsLatency = () => useStore(wsState, (s) => s.latencyMs);
